import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { findCustomerByCipp } from '../utils/findCustomerByCipp';

const TenantContext = createContext(null);

/**
 * TenantProvider manages the currently selected customer/tenant
 * This is used throughout the dashboard to filter data to a specific tenant
 *
 * Also manages Odin-MCP customer mappings for hybrid platform ID resolution:
 * - DIRECT mode: When customer selected, platform IDs are passed directly to agent
 * - FUZZY mode: When "All Customers" selected, agent uses discover_customer
 */
export const TenantProvider = ({ children }) => {
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [customerMappings, setCustomerMappings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [knowledgeBaseMode, setKnowledgeBaseMode] = useState(false);

  /**
   * Derived agent mode based on current selection state:
   * - 'knowledge': Knowledge Base selected from dropdown
   * - 'direct': A specific tenant is selected (platform IDs pre-filled)
   * - 'all': All Customers view (fuzzy mode, must discover)
   */
  const agentMode = useMemo(() => {
    if (knowledgeBaseMode) return 'knowledge';
    if (selectedTenant) return 'direct';
    return 'all';
  }, [knowledgeBaseMode, selectedTenant]);

  /**
   * Select a tenant by ID
   */
  const selectTenant = useCallback((tenantId) => {
    setKnowledgeBaseMode(false);
    if (tenantId === null) {
      setSelectedTenant(null);
      return;
    }

    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setSelectedTenant(tenant);
    }
  }, [tenants]);

  /**
   * Select a tenant by object
   */
  const selectTenantObject = useCallback((tenant) => {
    setSelectedTenant(tenant);
  }, []);

  /**
   * Clear the selected tenant (show all tenants view)
   */
  const clearSelection = useCallback(() => {
    setSelectedTenant(null);
    setKnowledgeBaseMode(false);
  }, []);

  /**
   * Enter knowledge base mode - clears tenant selection
   */
  const enterKnowledgeMode = useCallback(() => {
    setKnowledgeBaseMode(true);
    setSelectedTenant(null);
  }, []);

  /**
   * Update the tenants list (called when data is fetched from CSG Gateway)
   */
  const updateTenants = useCallback((newTenants) => {
    setTenants(newTenants);

    // If currently selected tenant is no longer in the list, clear selection
    if (selectedTenant && !newTenants.find(t => t.id === selectedTenant.id)) {
      setSelectedTenant(null);
    }
  }, [selectedTenant]);

  /**
   * Check if viewing all tenants (no specific selection)
   */
  const isAllTenantsView = useMemo(() => {
    return selectedTenant === null;
  }, [selectedTenant]);

  /**
   * Get platform IDs for the currently selected customer
   * Returns null if no customer selected (use FUZZY mode)
   * Returns platformIds object if customer is selected and mapped (use DIRECT mode)
   *
   * @returns {Object|null} Platform IDs object or null
   * {
   *   cipp: "uuid-string",
   *   sophos: "uuid-string" | null,
   *   acronis: "uuid-string" | null,
   *   autotask: number | null,
   *   scalepad: "uuid-string" | null
   * }
   */
  const getSelectedPlatformIds = useCallback(() => {
    if (!selectedTenant?.id) return null;

    // The mapping for the selected CIPP tenant id, or for an odin: key (no managed M365)
    const mapping = findCustomerByCipp(customerMappings, selectedTenant.id);

    return mapping?.platformIds || null;
  }, [selectedTenant, customerMappings]);

  /**
   * Update customer mappings from Odin-MCP
   */
  const updateCustomerMappings = useCallback((mappings) => {
    setCustomerMappings(mappings);
  }, []);

  const value = useMemo(() => ({
    // State
    selectedTenant,
    tenants,
    customerMappings,
    isLoading,
    error,
    isAllTenantsView,
    agentMode,
    knowledgeBaseMode,

    // Actions
    selectTenant,
    selectTenantObject,
    clearSelection,
    enterKnowledgeMode,
    updateTenants,
    updateCustomerMappings,
    getSelectedPlatformIds,
    setIsLoading,
    setError,
  }), [
    selectedTenant,
    tenants,
    customerMappings,
    isLoading,
    error,
    isAllTenantsView,
    agentMode,
    knowledgeBaseMode,
    selectTenant,
    selectTenantObject,
    clearSelection,
    enterKnowledgeMode,
    updateTenants,
    updateCustomerMappings,
    getSelectedPlatformIds,
  ]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

TenantProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export { TenantContext };
